import { supportProject, supportUrl } from '../config/support';

export type TicketCategory =
  | 'FundsNotReceived'
  | 'Suggestion'
  | 'LoginAccess'
  | 'PaymentIssue'
  | 'KycVerification'
  | 'ProfileData'
  | 'TechnicalIssue'
  | 'Registration'
  | 'PasswordRecovery'
  | 'AccountBlocked';

export interface AttachmentResponse {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: string;
}

export interface MessageResponse {
  id: string;
  ticketId: string;
  senderId: string | null;
  senderName: string | null;
  isGuest: boolean;
  body: string;
  isInternal: boolean;
  createdAt: string;
  attachments: AttachmentResponse[];
}

export interface MessagesPage {
  total: number;
  page: number;
  pageSize: number;
  items: MessageResponse[];
}

export type TicketStatus = 'Open' | 'InProgress' | 'WaitingUser' | 'Resolved' | 'Closed';

export interface SupportTicket {
  id: string;
  projectId: string;
  projectName: string;
  guestEmail: string;
  title: string;
  status: TicketStatus;
  priority: string;
  category: TicketCategory;
  assignedAgentName: string | null;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
}

export interface TicketsPage {
  total: number;
  page: number;
  pageSize: number;
  items: SupportTicket[];
}

export interface CreateTicketResponse {
  ticketId: string;
  ticketToken: string;
}

async function supportRequest<T>(
  path: string,
  options: RequestInit = {},
  token?: string | null,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Support-Project': supportProject,
    ...(options.headers as Record<string, string>),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${supportUrl}${path}`, { ...options, headers });
  const data = await res.json().catch(() => null);

  if (!res.ok) {
    throw new Error(data?.message || data?.error || `Ошибка HTTP ${res.status}`);
  }
  return data as T;
}

async function supportFormRequest<T>(
  path: string,
  form: FormData,
  token: string,
): Promise<T> {
  const headers: Record<string, string> = {
    'X-Support-Project': supportProject,
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${supportUrl}${path}`, {
    method: 'POST',
    headers,
    body: form,
  });
  const data = await res.json().catch(() => null);

  if (!res.ok) {
    throw new Error(data?.message || data?.error || `Ошибка HTTP ${res.status}`);
  }
  return data as T;
}

export function createTicket(req: {
  guestEmail: string;
  title: string;
  body: string;
  category: TicketCategory;
}, token: string): Promise<CreateTicketResponse> {
  return supportRequest<CreateTicketResponse>(
    '/api/tickets',
    { method: 'POST', body: JSON.stringify(req) },
    token,
  );
}

export function createAccessRecoveryTicket(req: {
  email: string;
  title?: string;
  body: string;
  category?: TicketCategory;
}): Promise<CreateTicketResponse> {
  return supportRequest<CreateTicketResponse>(
    '/api/tickets/access-recovery',
    { method: 'POST', body: JSON.stringify(req) },
    null,
  );
}

export function getMessages(ticketId: string, token: string): Promise<MessagesPage> {
  return supportRequest<MessagesPage>(
    `/api/tickets/${ticketId}/messages?page=1&pageSize=100`,
    {},
    token,
  );
}

export function getMyTickets(token: string): Promise<TicketsPage> {
  return supportRequest<TicketsPage>('/api/tickets/mine?page=1&pageSize=100', {}, token);
}

export function getTicket(ticketId: string, token: string): Promise<SupportTicket> {
  return supportRequest<SupportTicket>(`/api/tickets/${ticketId}`, {}, token);
}

export function sendMessage(
  ticketId: string,
  token: string,
  body: string,
): Promise<MessageResponse> {
  return supportRequest<MessageResponse>(
    `/api/tickets/${ticketId}/messages`,
    { method: 'POST', body: JSON.stringify({ body, isInternal: false }) },
    token,
  );
}

export function uploadAttachment(
  ticketId: string,
  token: string,
  file: File,
): Promise<AttachmentResponse> {
  const form = new FormData();
  form.append('file', file);
  return supportFormRequest<AttachmentResponse>(
    `/api/tickets/${ticketId}/attachments`,
    form,
    token,
  );
}

export async function downloadAttachmentBlob(
  attachmentId: string,
  token: string,
): Promise<Blob> {
  const res = await fetch(`${supportUrl}/api/attachments/${attachmentId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Support-Project': supportProject,
    },
  });
  if (!res.ok) {
    throw new Error(`Ошибка HTTP ${res.status}`);
  }
  return res.blob();
}
